/**
 * Asset storage abstraction for generation history.
 * Handles the BLOB-vs-file duality: in standalone/browser mode assets are
 * stored as SQLite BLOBs; in embedded mode they can live on disk.
 *
 * Mirrors Unity's 3-tier pattern: asset_path (disk) → asset_data (BLOB).
 * In standalone mode cacheDir is always null, so BLOBs are always used.
 */

// ─── Byte cache (avoids re-fetching blob URLs on save) ──────────────────
const blobByteCache = new Map<string, Uint8Array>();

export function cacheAssetBytes(url: string, bytes: Uint8Array): void {
  blobByteCache.set(url, bytes);
}
export function getCachedAssetBytes(url: string): Uint8Array | undefined {
  return blobByteCache.get(url);
}
export function removeCachedAssetBytes(url: string): void {
  blobByteCache.delete(url);
}
/** Revoke a single asset blob URL and remove its cached bytes. */
export function revokeAssetUrl(url: string): void {
  blobByteCache.delete(url);
  try { URL.revokeObjectURL(url); } catch { /* ok — may already be revoked */ }
}
export function clearAssetByteCache(): void {
  // Revoke all blob URLs we created before clearing the cache
  for (const url of blobByteCache.keys()) {
    try { URL.revokeObjectURL(url); } catch { /* ok */ }
  }
  blobByteCache.clear();
}

/** Create a Blob URL from raw bytes, caching the bytes for later retrieval */
export function createAssetUrl(data: Uint8Array, mimeType: string): string {
  const copy = new Uint8Array(data);
  const blob = new Blob([copy], { type: mimeType });
  const url = URL.createObjectURL(blob);
  blobByteCache.set(url, copy);
  return url;
}

/** Read asset bytes — cache hit avoids fetch + allocation */
export async function readAssetBytes(objectUrl: string): Promise<Uint8Array | null> {
  const cached = blobByteCache.get(objectUrl);
  if (cached) return cached;
  try {
    const resp = await fetch(objectUrl);
    const buf = await resp.arrayBuffer();
    return new Uint8Array(buf);
  } catch {
    return null;
  }
}

// ─── Persisted history tracking (for blob URL pruning) ──────────────────
const persistedHistoryIds = new Set<string>();

export function markHistoryPersisted(id: string): void { persistedHistoryIds.add(id); }
export function markHistoryPersistedBatch(ids: Iterable<string>): void {
  for (const id of ids) persistedHistoryIds.add(id);
}
export function isHistoryPersisted(id: string): boolean { return persistedHistoryIds.has(id); }
export function clearPersistedHistoryTracking(): void { persistedHistoryIds.clear(); }

/**
 * Resolve an asset on load: tries asset_path first (if cache dir available),
 * falls back to asset_data BLOB.
 *
 * In standalone browser mode, cacheDir is null → always uses BLOB.
 * In embedded mode, cacheDir is passed from Unity bridge.
 *
 * Returns a Blob URL, or null if neither source is available.
 */
export function resolveAsset(
  assetData: Uint8Array | null,
  _assetPath: string | null,
  mimeType: string,
  _cacheDir: string | null,
): string | null {
  // Future: if cacheDir and assetPath are both set, load from file.
  // For now, standalone mode only uses BLOBs.
  if (assetData && assetData.length > 0) {
    return createAssetUrl(assetData, mimeType);
  }
  return null;
}

/** Safe Uint8Array → ArrayBuffer: handles views with non-zero byteOffset */
export function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}
